<template>
  <div class="qa-page">
    <!-- Header -->
    <div class="qa-header">
      <div>
        <h1 class="qa-title">منتدى الأسئلة</h1>
        <p class="qa-subtitle">اقرأ الأسئلة، أضف إجابتك، وابحث في المنشورات بسهولة</p>
      </div>
      <v-chip v-if="pendingCount > 0" class="qa-waiting-chip qa-waiting-chip--urgent">
        <v-icon left small class="qa-icon-in-btn">mdi-comment-alert-outline</v-icon>
        {{ pendingCount }} {{ pendingCount === 1 ? "سؤال" : "أسئلة" }} بانتظار إجابتك
      </v-chip>
      <v-chip v-else-if="waitingResponseCount !== null" class="qa-waiting-chip">
        {{ waitingResponseCount }} سؤال بانتظار رد
      </v-chip>
    </div>

    <!-- Required answer banner -->
    <v-alert
      v-if="showRequiredBanner"
      type="warning"
      prominent
      border="left"
      colored-border
      class="qa-required-alert mb-4"
      icon="mdi-alert-circle-outline"
    >
      <div class="font-weight-bold mb-1">يجب الإجابة على الأسئلة أولاً</div>
      <div>
        لديك {{ pendingCount }} {{ pendingCount === 1 ? "سؤال" : "أسئلة" }} بانتظار إجابتك.
        يرجى قراءة الأسئلة المميزة أدناه وإضافة تعليقك قبل متابعة أي عمل آخر.
      </div>
    </v-alert>


    <!-- =================================== Search Component ===================================-->
    <question-and-answer-search-component :filters.sync="filters" :all-users="allUsers" :active-tab="activeTab"
      @apply-filters="applyFilters" @reset-filters="resetFilters" @switch-tab="switchTab" :isAdmin=false />
    <!-- ======================================================================================== -->

    <!-- Loading -->
    <div v-if="loading" class="qa-loading">
      <v-progress-circular indeterminate size="48" width="4" color="#5c325e" />
      <span>جاري التحميل...</span>
    </div>

    <!-- Empty -->
    <v-card v-else-if="posts.length === 0" class="qa-card qa-empty" elevation="0">
      <v-icon size="56" class="qa-icon-muted">mdi-forum-outline</v-icon>
      <p>{{ viewTab === 'pending' ? 'لا توجد أسئلة بانتظار إجابتك' : 'لا توجد منشورات مطابقة للبحث' }}</p>
      <button type="button" class="qa-btn qa-btn-outline" @click="resetFilters">عرض الكل</button>
    </v-card>

    <!-- Posts -->
    <div v-else class="qa-posts">
      <v-card
        v-for="post in posts"
        :key="post.id"
        class="qa-card qa-post"
        :class="{ 'qa-post--pending': isPendingAnswer(post) }"
        elevation="0"
      >
        <div v-if="isPendingAnswer(post)" class="qa-pending-badge">
          <v-icon size="16" class="qa-icon-in-btn">mdi-comment-alert-outline</v-icon>
          بانتظار إجابتك
        </div>

        <div class="qa-post-header">
          <div class="qa-post-author">
            <v-avatar color="#5c325e" size="48" class="qa-avatar-sm">
              <span class="qa-avatar-text">{{ getInitials(post.user?.name) }}</span>
            </v-avatar>
            <div>
              <p class="qa-post-name">{{ post.user?.name || 'مستخدم' }}</p>
              <p class="qa-post-date">{{ post.created_at }}</p>
            </div>
          </div>
          <div class="qa-post-meta">
            <button type="button" class="qa-like-chip" @click="listQuestionLikes(post.id)">
              <v-icon size="16" class="qa-icon-in-btn">mdi-thumb-up-outline</v-icon>
              {{ post.likes_count }}
            </button>
          </div>
        </div>

        <p class="qa-post-question">{{ post.question }}</p>

        <div class="qa-post-actions">
          <button type="button" class="qa-text-btn" @click="toggleComments(post.id)">
            <v-icon size="20" class="qa-icon-accent">mdi-comment-text-outline</v-icon>
            التعليقات ({{ post.comments?.length || 0 }})
          </button>
          <v-spacer />
          <button type="button" class="qa-icon-btn" @click="likePost(post.id)">
            <v-icon size="22" :class="post.is_liked_by_user ? 'qa-icon-accent' : 'qa-icon-muted'">
              {{ post.is_liked_by_user ? 'mdi-thumb-up' : 'mdi-thumb-up-outline' }}
            </v-icon>
          </button>
        </div>

        <v-expand-transition>
          <div v-if="expandedPostId === post.id" class="qa-comments">
            <div v-for="comment in post.comments" :key="comment.id" class="qa-comment">
              <div class="qa-comment-top">
                <span class="qa-comment-author">{{ comment.user?.name }}</span>
                <span class="qa-comment-date">{{ comment.created_at }}</span>
              </div>
              <p class="qa-comment-text">{{ comment.answer }}</p>
              <div class="qa-comment-actions">
                <button type="button" class="qa-like-chip qa-like-chip--sm" @click="listCommentLikes(comment.id)">
                  <v-icon size="14" class="qa-icon-in-btn">mdi-thumb-up-outline</v-icon>
                  {{ comment.likes_count }}
                </button>
                <button type="button" class="qa-icon-btn" @click="likeComment(comment.id)">
                  <v-icon size="18" :class="comment.is_liked_by_user ? 'qa-icon-accent' : 'qa-icon-muted'">
                    {{ comment.is_liked_by_user ? 'mdi-thumb-up' : 'mdi-thumb-up-outline' }}
                  </v-icon>
                </button>
              </div>
            </div>

            <div v-if="canSendComment" class="qa-add-comment">
              <v-textarea
                v-model="newComment"
                placeholder="أضف إجابتك هنا..."
                rows="2"
                auto-grow
                hide-details
                outlined
                class="qa-field"
              />
              <button type="button" class="qa-btn qa-btn-primary mt-3" @click="addComment(post.id)">
                <v-icon size="18" class="qa-icon-in-btn">mdi-send</v-icon>
                إرسال الإجابة
              </button>
            </div>
          </div>
        </v-expand-transition>
      </v-card>
    </div>

    <!-- Pagination -->
    <div v-if="pages > 1" class="qa-pagination">
      <button type="button" class="qa-icon-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
        <v-icon size="28" class="qa-icon-accent">mdi-chevron-right</v-icon>
      </button>
      <span class="qa-pagination-text">صفحة {{ currentPage }} من {{ pages }} — {{ results }} منشور</span>
      <button type="button" class="qa-icon-btn" :disabled="currentPage === pages" @click="changePage(currentPage + 1)">
        <v-icon size="28" class="qa-icon-accent">mdi-chevron-left</v-icon>
      </button>
    </div>

    <!-- Likes dialog -->
    <v-dialog v-model="dialogOfQuestionLikeusers" max-width="560">
      <v-card class="qa-card">
        <v-card-title class="qa-dialog-title">
          <v-icon size="24" class="qa-icon-accent ml-2">mdi-thumb-up</v-icon>
          {{ likeText }}
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 360px; overflow-y: auto">
          <v-row dense>
            <v-col v-for="user in likedUsers" :key="user.id" cols="12" sm="6" class="d-flex align-center">
              <v-avatar color="#5c325e" size="44" class="ml-3">
                <span class="qa-avatar-text">{{ getInitials(user.name) }}</span>
              </v-avatar>
              <div>
                <p class="mb-0 font-weight-medium">{{ user.name }}</p>
                <small class="qa-muted">{{ user.job_title || '—' }}</small>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <button type="button" class="qa-btn qa-btn-outline" @click="dialogOfQuestionLikeusers = false">إغلاق</button>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { axiosInstance } from "@/axios/config";
import {
  applyPendingQuestionnaireState,
  refreshPendingQuestionnaire,
} from "@/utils/fundingQuestionnaireGuard";
import QuestionAndAnswerSearchComponent from "@/components/QuestionAndAnswerSearchComponent.vue";

const defaultFilters = () => ({
  search: "",
  author_user_id: null,
  comment_user_id: null,
  has_comments: "all",
  has_likes: "all",
  has_comment_likes: "all",
});

export default {
  components: {
    QuestionAndAnswerSearchComponent,
  },
  data() {
    return {
      viewTab: "all",
      posts: [],
      expandedPostId: null,
      newComment: "",
      loading: false,
      waitingResponseCount: null,
      allUsers: [],
      allowedUsersForAnswers: [],
      filters: defaultFilters(),
      triStateOptions: [
        { label: "الكل", value: "all" },
        { label: "نعم", value: "yes" },
        { label: "لا", value: "no" },
      ],
      pagination: { limit: 6, count: 0 },
      currentPage: 1,
      dialogOfQuestionLikeusers: false,
      likedUsers: [],
      likeText: "",
    };
  },

  computed: {
    results() {
      return this.pagination.count;
    },
    pages() {
      return Math.max(1, Math.ceil(this.pagination.count / this.pagination.limit));
    },
    canSendComment() {
      const cookie = document.cookie
        .split(";")
        .map((c) => c.split("="))
        .reduce(
          (acc, [key, val]) => ({ ...acc, [key.trim()]: decodeURIComponent(val) }),
          {}
        )["wsataJwt"];
      let authUserId = "";
      if (cookie !== undefined) {
        try {
          authUserId = JSON.parse(cookie).agent_id;
        } catch (e) {
          authUserId = "";
        }
      }
      return this.allowedUsersForAnswers.some((user) => user.id == authUserId);
    },
    showRequiredBanner() {
      return (
        this.$route.query.required === "1" ||
        this.$store.state.fundingManagerBlockedByQuestionnaire
      );
    },
    pendingCount() {
      return this.$store.state.pendingQuestionnaireCount || 0;
    },
  },

  async created() {
    if (this.$route.query.required === "1") {
      this.$store.state.showFundingQuestionnaireRequiredAlert = true;
      this.viewTab = "pending";
    }
    await this.loadUsers();
    await this.getAllowedUsersForAnswers();
    await this.fetchPosts(1);
  },

  methods: {
    getInitials(fullName) {
      if (!fullName) return "?";
      return fullName
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p.charAt(0).toUpperCase())
        .join("");
    },

    isPendingAnswer(post) {
      if (!this.canSendComment) return false;
      const allowedUserIds = this.allowedUsersForAnswers.map((user) => user.id);
      const hasAllowedAnswer = (post.comments || []).some(
        (comment) => comment.user && allowedUserIds.includes(comment.user.id)
      );
      return !hasAllowedAnswer;
    },

    focusFirstPendingQuestion() {
      const pendingPost = this.posts.find((post) => this.isPendingAnswer(post));
      if (pendingPost) {
        this.expandedPostId = pendingPost.id;
        this.$nextTick(() => {
          const element = document.querySelector(".qa-post--pending");
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        });
      }
    },

    buildSearchParams() {
      const params = {
        per_page: this.pagination.limit,
        page: this.currentPage,
      };
      if (this.filters.search?.trim()) params.search = this.filters.search.trim();
      if (this.filters.author_user_id) params.author_user_id = this.filters.author_user_id;
      if (this.filters.comment_user_id) params.comment_user_id = this.filters.comment_user_id;
      ["has_comments", "has_likes", "has_comment_likes"].forEach((key) => {
        if (this.filters[key] && this.filters[key] !== "all") {
          params[key] = this.filters[key];
        }
      });
      if (this.viewTab === "pending") {
        params.pending_answer = "yes";
      }
      return params;
    },

    async loadUsers() {
      try {
        const res = await axiosInstance.get("/agent/list-listAllUserForAnswer");
        const block = res.data.payload;
        this.allUsers = Array.isArray(block) ? block : block?.data || [];
      } catch (e) {
        console.error(e);
      }
    },

    async getAllowedUsersForAnswers() {
      try {
        const res = await axiosInstance.get("/agent/list-allowedUserForAnswer", {
          headers: this.request_headers(),
        });
        const block = res.data.payload;
        this.allowedUsersForAnswers = Array.isArray(block) ? block : block?.data || [];
      } catch (e) {
        console.error(e);
      }
    },

    async fetchPosts(page = 1) {
      this.currentPage = page;
      this.loading = true;
      try {
        const res = await axiosInstance.get("/employee/funding-manager/list-questionaires", {
          params: this.buildSearchParams(),
        });
        const block = res.data.payload.data;
        this.posts = Array.isArray(block) ? block : block?.data || [];
        this.pagination.count = res.data.payload.count || 0;
        this.waitingResponseCount =
          res.data.payload.count_of_question_waiting_personal_response ??
          res.data.payload.count_of_question_waiting_response ??
          null;
        this.$nextTick(() => {
          if (this.showRequiredBanner || this.viewTab === "pending") {
            this.focusFirstPendingQuestion();
          }
        });
      } catch (error) {
        this.showPopup("error", error.response?.data?.message || "خطأ أثناء جلب المنشورات.");
      } finally {
        this.loading = false;
      }
    },

    switchView(tab) {
      this.viewTab = tab;
      this.currentPage = 1;
      this.fetchPosts(1);
    },

    applyFilters() {
      this.currentPage = 1;
      this.fetchPosts(1);
    },

    resetFilters() {
      this.filters = defaultFilters();
      this.viewTab = "all";
      this.applyFilters();
    },

    changePage(newPage) {
      if (newPage < 1 || newPage > this.pages) return;
      this.fetchPosts(newPage);
    },

    toggleComments(postId) {
      this.expandedPostId = this.expandedPostId === postId ? null : postId;
    },

    async likePost(postId) {
      try {
        const res = await axiosInstance.get(
          `/employee/funding-manager/like-questionaire/${postId}`
        );
        this.showPopup("success", res.data.message);
        await this.fetchPosts(this.currentPage);
      } catch (res) {
        this.showPopup("error", res.response?.data?.message);
      }
    },

    async likeComment(commentId) {
      try {
        const res = await axiosInstance.get(
          `/employee/funding-manager/like-comment/${commentId}`
        );
        this.showPopup("success", res.data.message);
        await this.fetchPosts(this.currentPage);
      } catch (res) {
        this.showPopup("error", res.response?.data?.message);
      }
    },

    async addComment(postId) {
      if (!this.newComment.trim()) {
        this.showPopup("error", "يرجى كتابة تعليق قبل الإرسال.");
        return;
      }
      try {
        const res = await axiosInstance.post(
          `/employee/funding-manager/answer-questionaires/${postId}`,
          { answer: this.newComment }
        );
        this.showPopup("success", res.data.message);
        this.newComment = "";
        await this.fetchPosts(this.currentPage);
        await refreshPendingQuestionnaire(true);

        if (res.data.payload && res.data.payload.id) {
          applyPendingQuestionnaireState(res.data.payload);
          this.$store.state.showFundingQuestionnaireRequiredAlert = true;
          this.viewTab = "pending";
          this.focusFirstPendingQuestion();
        } else {
          applyPendingQuestionnaireState(null);
          this.$store.state.showFundingQuestionnaireRequiredAlert = false;
          this.showPopup("success", "تم إكمال جميع الأسئلة! يمكنك الآن متابعة عملك.");
          if (this.$route.query.required === "1") {
            this.$router.replace({ name: "fundingMangerHome" });
          }
        }
      } catch (res) {
        this.showPopup("error", res.response?.data?.message);
      }
    },

    async listQuestionLikes(questionId) {
      this.likeText = "المعجبين بالسؤال";
      try {
        const res = await axiosInstance.get(
          `/employee/funding-manager/list-question-likes/${questionId}`
        );
        this.likedUsers = res.data.payload.data || [];
        this.dialogOfQuestionLikeusers = true;
      } catch (res) {
        this.showPopup("error", res.response?.data?.message);
      }
    },

    async listCommentLikes(commentId) {
      this.likeText = "المعجبين بالتعليق";
      try {
        const res = await axiosInstance.get(
          `/employee/funding-manager/list-comment-likes/${commentId}`
        );
        this.likedUsers = res.data.payload.data || [];
        this.dialogOfQuestionLikeusers = true;
      } catch (res) {
        this.showPopup("error", res.response?.data?.message);
      }
    },
  },
};
</script>

<style scoped>
.qa-page {
  --qa-bg: #eef2f7;
  --qa-card: #ffffff;
  --qa-border: #cbd5e1;
  --qa-text: #0f172a;
  --qa-muted: #475569;
  --qa-accent: #5c325e;
  --qa-accent-hover: #4a284c;
  --qa-accent-soft: rgba(92, 50, 94, 0.1);
  --qa-warning: #ea580c;
  --qa-warning-soft: #fff7ed;
  --qa-warning-border: #fb923c;
  --qa-btn-text: #ffffff;
  --qa-outline-text: #5c325e;
  --qa-icon: #5c325e;
  --qa-icon-muted: #64748b;
  --qa-segment-bg: #f1f5f9;
  padding: 28px 24px 40px;
  max-width: 1120px;
  margin: 0 auto;
  font-size: 15px;
}

.dark .qa-page {
  --qa-bg: transparent;
  --qa-card: #1e293b;
  --qa-border: #475569;
  --qa-text: #f8fafc;
  --qa-muted: #94a3b8;
  --qa-accent: #c084fc;
  --qa-accent-hover: #a855f7;
  --qa-accent-soft: rgba(192, 132, 252, 0.15);
  --qa-warning-soft: rgba(234, 88, 12, 0.12);
  --qa-warning-border: #fb923c;
  --qa-btn-text: #ffffff;
  --qa-outline-text: #e9d5ff;
  --qa-icon: #c084fc;
  --qa-icon-muted: #94a3b8;
  --qa-segment-bg: #0f172a;
}

.qa-icon-accent { color: var(--qa-icon) !important; }
.qa-icon-muted { color: var(--qa-icon-muted) !important; }
.qa-icon-in-btn { color: inherit !important; }

.qa-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background 0.2s, color 0.2s, border-color 0.2s, opacity 0.2s;
  white-space: nowrap;
}
.qa-btn-primary { background: var(--qa-accent); color: var(--qa-btn-text) !important; }
.qa-btn-primary:hover { background: var(--qa-accent-hover); }
.qa-btn-outline {
  background: var(--qa-card);
  color: var(--qa-outline-text) !important;
  border: 2px solid var(--qa-accent);
}
.qa-btn-outline:hover { background: var(--qa-accent-soft); }

.qa-text-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: var(--qa-accent) !important;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
}
.qa-text-btn:hover { background: var(--qa-accent-soft); }

.qa-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  color: var(--qa-icon);
}
.qa-icon-btn:hover:not(:disabled) { background: var(--qa-accent-soft); }
.qa-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.qa-like-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid var(--qa-border);
  background: var(--qa-card);
  color: var(--qa-text);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
}
.qa-like-chip:hover { border-color: var(--qa-accent); background: var(--qa-accent-soft); }
.qa-like-chip--sm { padding: 4px 10px; font-size: 0.82rem; }

.qa-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.qa-title { font-size: 1.75rem; font-weight: 700; color: var(--qa-text); margin: 0 0 6px; }
.qa-subtitle { color: var(--qa-muted); margin: 0; font-size: 1rem; }

.qa-waiting-chip {
  background: var(--qa-accent-soft) !important;
  color: var(--qa-accent) !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  padding: 8px 14px !important;
  height: auto !important;
}
.qa-waiting-chip--urgent {
  background: var(--qa-warning-soft) !important;
  color: var(--qa-warning) !important;
  border: 1px solid var(--qa-warning-border) !important;
}

.qa-required-alert { border-radius: 12px !important; }

.qa-card {
  background: var(--qa-card) !important;
  border: 1px solid var(--qa-border);
  border-radius: 14px !important;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}
.dark .qa-card { box-shadow: none; }

.qa-avatar-text { color: #fff !important; font-weight: 700; font-size: 1rem; }

.qa-filters-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.1rem !important;
  font-weight: 700 !important;
  color: var(--qa-text) !important;
  padding: 16px 20px !important;
}
.qa-filters-body { padding: 20px 20px 8px !important; }

.qa-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  background: var(--qa-segment-bg);
  border-radius: 0 0 14px 14px;
}
.qa-toolbar-group { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
.qa-toolbar-label { font-size: 0.9rem; font-weight: 700; color: var(--qa-muted); white-space: nowrap; }
.qa-toolbar-actions { gap: 10px; }

.qa-segmented {
  display: inline-flex;
  padding: 4px;
  background: var(--qa-card);
  border: 1px solid var(--qa-border);
  border-radius: 12px;
  gap: 4px;
}
.qa-segmented-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: var(--qa-muted);
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
}
.qa-segmented-btn:hover:not(.qa-segmented-btn--active) {
  background: var(--qa-accent-soft);
  color: var(--qa-accent);
}
.qa-segmented-btn--active {
  background: var(--qa-accent);
  color: #fff !important;
  box-shadow: 0 2px 8px rgba(92, 50, 94, 0.25);
}
.qa-segmented-btn--active .qa-icon-in-btn { color: #fff !important; }

.qa-loading, .qa-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 56px 24px;
  color: var(--qa-muted);
  font-size: 1.05rem;
}

.qa-post { position: relative; overflow: hidden; }
.qa-post--pending {
  border: 2px solid var(--qa-warning-border) !important;
  background: var(--qa-warning-soft) !important;
  box-shadow: 0 4px 14px rgba(234, 88, 12, 0.12) !important;
}
.qa-pending-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 16px 20px 0;
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--qa-warning);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
}

.qa-post-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 20px 0;
  gap: 16px;
}
.qa-post-author { display: flex; gap: 14px; align-items: center; }
.qa-post-name { margin: 0; font-weight: 700; font-size: 1.05rem; color: var(--qa-text); }
.qa-post-date, .qa-comment-date, .qa-muted { color: var(--qa-muted); font-size: 0.85rem; }
.qa-post-meta { display: flex; align-items: center; gap: 8px; }
.qa-post-question {
  padding: 16px 20px;
  margin: 0;
  font-size: 1.08rem;
  line-height: 1.75;
  color: var(--qa-text);
}
.qa-post-actions {
  display: flex;
  align-items: center;
  padding: 12px 16px 16px;
  border-top: 1px solid var(--qa-border);
}
.qa-comments {
  border-top: 1px solid var(--qa-border);
  background: var(--qa-accent-soft);
  padding: 16px 20px 20px;
}
.qa-comment {
  background: var(--qa-card);
  border: 1px solid var(--qa-border);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.qa-comment-top { display: flex; justify-content: space-between; margin-bottom: 8px; gap: 12px; }
.qa-comment-author { font-weight: 700; font-size: 0.92rem; color: var(--qa-text); }
.qa-comment-text { margin: 0 0 10px; color: var(--qa-text); font-size: 0.98rem; line-height: 1.65; }
.qa-comment-actions { display: flex; align-items: center; gap: 8px; }
.qa-add-comment { margin-top: 12px; }

.qa-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 20px 0 36px;
}
.qa-pagination-text { color: var(--qa-muted); font-size: 1rem; font-weight: 600; }
.qa-dialog-title { color: var(--qa-text) !important; font-size: 1.15rem !important; font-weight: 700 !important; }

.qa-page >>> .v-input__slot { background: var(--qa-card) !important; min-height: 48px !important; }
.qa-page >>> fieldset { border-color: var(--qa-border) !important; border-width: 1.5px !important; }
.qa-page >>> .v-label, .qa-page >>> input, .qa-page >>> textarea { color: var(--qa-text) !important; font-size: 0.95rem !important; }
.qa-page >>> .v-input__prepend-inner .v-icon { color: var(--qa-icon) !important; }
.dark .qa-page >>> .v-input__slot { background: #0f172a !important; }

@media (max-width: 768px) {
  .qa-page { padding: 16px 12px 32px; }
  .qa-toolbar { flex-direction: column; align-items: stretch; }
  .qa-toolbar-group, .qa-toolbar-actions { width: 100%; justify-content: center; }
  .qa-segmented { width: 100%; }
  .qa-segmented-btn { flex: 1; justify-content: center; padding: 10px 8px; font-size: 0.85rem; }
  .qa-btn { flex: 1; }
}
</style>
